import { motion } from 'framer-motion'
import {
  Users,
  BarChart3,
  Shield,
  User,
  RefreshCw,
  LogOut,
  LinkIcon,
  Menu,
  X,
  Mail,
  CreditCard,
  Database,
  HardDrive,
  UserCheck,
  Key,
  ClockIcon,
  GitBranch,
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { adminAuthService } from '../services/adminAuth'
import type { AdminUser } from '../services/adminAuth'

interface AdminHeaderProps {
  title: string
  subtitle?: string
  showRefresh?: boolean
  onRefresh?: () => void
  isRefreshing?: boolean
  lastUpdated?: Date | null
}

export default function AdminHeader({
  title,
  subtitle,
  showRefresh = false,
  onRefresh,
  isRefreshing = false,
  lastUpdated,
}: AdminHeaderProps) {
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const user = adminAuthService.getCurrentUser()
    setAdminUser(user)
  }, [])

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true)
      await adminAuthService.logout()
      toast.success('Logged out successfully')
      navigate('/login')
    } catch (error) {
      console.error('Logout failed:', error)
      toast.error('Logout failed')
    } finally {
      setIsLoggingOut(false)
    }
  }

  const handleRefresh = async () => {
    if (onRefresh) {
      await onRefresh()
    }
  }

  const navigationItems = [
    {
      name: 'Dashboard',
      href: '/',
      icon: BarChart3,
      current: location.pathname === '/',
    },
    {
      name: 'Analytics',
      href: '/analytics',
      icon: BarChart3,
      current: location.pathname === '/analytics',
    },
    {
      name: 'User Management',
      href: '/users',
      icon: Users,
      current: location.pathname.startsWith('/users'),
    },
    {
      name: 'Links',
      href: '/links',
      icon: LinkIcon,
      current: location.pathname === '/links',
    },
    {
      name: 'API Management',
      href: '/api-management',
      icon: Key,
      current: location.pathname === '/api-management',
    },
    {
      name: 'Plan Management',
      href: '/plans',
      icon: CreditCard,
      current: location.pathname === '/plans',
    },
    {
      name: 'Plan History',
      href: '/plan-history',
      icon: ClockIcon,
      current: location.pathname === '/plan-history',
    },
    {
      name: 'Subscription Management',
      href: '/subscriptions',
      icon: UserCheck,
      current: location.pathname === '/subscriptions',
    },
    {
      name: 'Email Management',
      href: '/email',
      icon: Mail,
      current: location.pathname === '/email',
    },
    {
      name: 'Backup Management',
      href: '/backup',
      icon: Database,
      current: location.pathname === '/backup',
    },
    {
      name: 'Capacity Management',
      href: '/capacity',
      icon: HardDrive,
      current: location.pathname === '/capacity',
    },
    {
      name: 'Security',
      href: '/security',
      icon: Shield,
      current:
        location.pathname === '/security' ||
        location.pathname.startsWith('/security/'),
      subItems: [
        {
          name: 'Security Settings',
          href: '/security',
          current: location.pathname === '/security',
        },
        {
          name: 'Security Logs',
          href: '/security/logs',
          current: location.pathname === '/security/logs',
        },
      ],
    },
    {
      name: 'Version Management',
      href: '/version',
      icon: GitBranch,
      current: location.pathname === '/version',
    },
  ]

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="border-b border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Top Header */}
        <div className="flex h-16 items-center justify-between">
          {/* Left side - Logo and Navigation */}
          <div className="flex items-center space-x-8">
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600">
                  <span className="text-sm font-bold text-white">S</span>
                </div>
                <span className="ml-2 text-xl font-bold text-gray-900 dark:text-white">
                  Shorty Admin
                </span>
              </Link>
            </div>

            {/* Navigation Menu */}
            <nav className="hidden space-x-6 md:flex">
              {navigationItems.map(item => (
                <div key={item.name} className="group relative">
                  <Link
                    to={item.href}
                    className={`inline-flex h-10 w-10 items-center justify-center rounded-lg text-sm font-medium transition-all duration-200 ${
                      item.current
                        ? 'border-2 border-indigo-600 bg-indigo-50 text-indigo-600 dark:border-indigo-400 dark:bg-indigo-900/50 dark:text-indigo-400'
                        : 'border-2 border-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200'
                    }`}
                  >
                    <item.icon className="h-5 w-5" />
                  </Link>

                  {/* Tooltip */}
                  <div className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 -translate-x-1/2 transform whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100 dark:bg-gray-700">
                    {item.name}
                    <div className="absolute bottom-full left-1/2 h-0 w-0 -translate-x-1/2 transform border-b-4 border-l-4 border-r-4 border-b-gray-900 border-l-transparent border-r-transparent dark:border-b-gray-700"></div>
                  </div>
                </div>
              ))}
            </nav>
          </div>

          {/* Right side - Admin info and actions */}
          <div className="flex items-center space-x-2">
            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 dark:hover:bg-gray-700 md:hidden"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>

            {showRefresh && onRefresh && (
              <div className="group relative">
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-lg border-2 border-transparent text-gray-500 transition-all duration-200 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                >
                  <RefreshCw
                    className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`}
                  />
                </button>
                <div className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 -translate-x-1/2 transform whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100 dark:bg-gray-700">
                  Refresh
                  <div className="absolute bottom-full left-1/2 h-0 w-0 -translate-x-1/2 transform border-b-4 border-l-4 border-r-4 border-b-gray-900 border-l-transparent border-r-transparent dark:border-b-gray-700"></div>
                </div>
              </div>
            )}

            <div className="group relative">
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border-2 border-transparent text-gray-500 transition-all duration-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:text-gray-400 dark:hover:bg-red-900/30 dark:hover:text-red-400"
              >
                {isLoggingOut ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-gray-600"></div>
                ) : (
                  <LogOut className="h-5 w-5" />
                )}
              </button>
              <div className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 -translate-x-1/2 transform whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100 dark:bg-gray-700">
                Logout
                <div className="absolute bottom-full left-1/2 h-0 w-0 -translate-x-1/2 transform border-b-4 border-l-4 border-r-4 border-b-gray-900 border-l-transparent border-r-transparent dark:border-b-gray-700"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-gray-200 dark:border-gray-700 md:hidden"
          >
            <div className="space-y-1 px-2 pb-3 pt-2 sm:px-3">
              {navigationItems.map(item => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                    item.current
                      ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200'
                  }`}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              ))}

              {/* Mobile admin info */}
              <div className="mt-3 border-t border-gray-200 pt-3 dark:border-gray-700">
                <div className="flex items-center px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                  <User className="mr-2 h-4 w-4" />
                  <span>{adminUser?.name || adminUser?.email || 'Admin'}</span>
                </div>

                {showRefresh && onRefresh && (
                  <button
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    className="flex w-full items-center rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    <RefreshCw
                      className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
                    />
                    Refresh
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Page Title Section */}
        <div className="border-t border-gray-200 py-6 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl">
                {title}
              </h1>
              {subtitle && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {subtitle}
                </p>
              )}
              {adminUser && (
                <p className="mt-1 text-xs text-indigo-600 dark:text-indigo-400">
                  Logged in as: {adminUser.email}
                </p>
              )}
              {lastUpdated && (
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  Last updated: {lastUpdated.toLocaleTimeString()}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
